import React, { useEffect, useState } from "react";

// ASSETS
import LeftArrow from "assets/icons/ArrowLeftHovered.svg";
import RedLoop from "assets/svg/SearchIconRed.svg";
import CoverPlaceholder from "assets/img/CoverPlaceholder.png";
import { ReactComponent as NoDataDark } from "assets/svg/NoDataDark.svg";

// STYLES
import {
  BackToButton,
  BackToImage,
  BackToWrapper,
  ContentSectionWrapper,
  HeaderSectionWrapper,
  ImgStyled,
  InputContainer,
  NoDataContainer,
  ProjectCardWrapper,
  ProjectCardsContainer,
  ProjectsContainer,
  SvgContainer,
  TitleSectionWrapper,
  TitleWrapper,
} from "./ProjectsStyled";

// LIBRARIES
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// MISC
import { IProps } from "./ProjectsModel";
import { ButtonType } from "components/Atoms/ButtonAtom/ButtonAtomModel";
import { OptionType } from "components/Atoms/InputAtom/InputAtomModel";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { COLORS } from "constants/variables";
import { ProjectCardType } from "components/ProjectCard/ProjectCardModel";
import { appRoutes } from "config/AppRoutesConfig";

// REDUX
import { useSelector } from "react-redux";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";

// COMPONENTS
import CustomText from "components/Atoms/CustomText/CustomText";
import usePagination from "hooks/usePagination";
import InputAtom from "components/Atoms/InputAtom/InputAtom";
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import HeaderTab from "components/HeaderTab/HeaderTab";
import Pagination from "components/Atoms/Paginations/Pagination";
import ProjectCard from "components/ProjectCard/ProjectCard";
import newsAndMediaService from "services/newsAndMediaService";
import { uploadBaseUrl } from "services/base";

const Projects = (props: IProps) => {
  // PROPERTIES
  const ELEMENTS_PER_PAGE = 9;

  // LIBRARY CONSTANTS
  const navigate = useNavigate();
  const { t } = useTranslation();
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [inputText, setInputText] = useState<string>("");
  const [options, setOptions] = useState<OptionType[]>([]);
  const [currentProjects, setCurrentProjects] = useState<{
    data: any[];
  }>({
    data: [],
  });
  const [pastProjects, setPastProjects] = useState<{
    data: any[];
  }>({
    data: [],
  });

  const {
    currentPage,
    totalPages: currentTotalPages,
    currentData: currentProjectsData,
    handlePageChange: handleCurrentPageChange,
  } = usePagination(currentProjects?.data, ELEMENTS_PER_PAGE);

  const {
    currentPage: pastPage,
    totalPages: pastTotalPages,
    currentData: pastProjectsData,
    handlePageChange: handlePastPageChange,
  } = usePagination(pastProjects?.data, ELEMENTS_PER_PAGE);
  // STATE CONSTANTS

  useEffect(() => {
    if (selectedTab === 0) {
      newsAndMediaService
        .getProjects(currentPage, ELEMENTS_PER_PAGE, inputText, {
          current: true,
        })
        .then((data) => {
          setCurrentProjects(data);
        })
        .catch((err) => console.error(err));
    } else {
      newsAndMediaService
        .getProjects(pastPage, ELEMENTS_PER_PAGE, inputText, { past: true })
        .then((data) => {
          setPastProjects(data);
        })
        .catch((err) => console.error(err));
    }
  }, [selectedTab, currentPage, pastPage, inputText]);



  const handleSearch = () => {
    if (inputText.length > 0) {
      if (selectedTab === 0) {
        handleCurrentPageChange(0);
        newsAndMediaService
          .getProjects(0, ELEMENTS_PER_PAGE, inputText, { current: true })
          .then((data) => {
            setCurrentProjects(data);
          })
          .catch((err) => console.error(err));
      } else {

        handlePastPageChange(0); 
        newsAndMediaService
          .getProjects(0, ELEMENTS_PER_PAGE, inputText, { past: true })
          .then((data) => {
            setPastProjects(data);
          })
          .catch((err) => console.error(err));
      }
    }
  };
  const ongoingProjects =
    currentProjects?.data.length > 0 ? (
      <ProjectCardsContainer>
        {currentProjects?.data &&
          currentProjects?.data.map((project) => (
            <ProjectCardWrapper key={project.id}>
              <ProjectCard
                id={project.id}
                image={
                  project?.attributes?.cover?.data?.attributes?.url?.trim()
                    ? `${uploadBaseUrl}${project?.attributes?.cover?.data?.attributes?.url}`
                    : CoverPlaceholder
                }
                text={project?.attributes?.title}
                type={ProjectCardType.PROJECTS_PAGE}
              />
            </ProjectCardWrapper>
          ))}
      </ProjectCardsContainer>
    ) : (
      <NoDataContainer>
        <SvgContainer>
          <NoDataDark />
        </SvgContainer>
        <CustomText
          text={t("app.noData")}
          type={TextType.P1}
          color={COLORS.MetalicSilver}
        />
      </NoDataContainer>
    );

  //eslint-disable-next-line
  const finishedProjects =
    pastProjects?.data.length > 0 ? (
      <ProjectCardsContainer>
        {pastProjects?.data &&
          pastProjects?.data.map((project) => (
            <ProjectCardWrapper key={project.id}>
              <ProjectCard
                id={project.id}
                image={
                  project?.attributes?.cover?.data?.attributes?.url?.trim()
                    ? `${uploadBaseUrl}${project?.attributes?.cover?.data?.attributes?.url}`
                    : CoverPlaceholder
                }
                text={project?.attributes?.title}
                type={ProjectCardType.PROJECTS_PAGE}
              />
            </ProjectCardWrapper>
          ))}
      </ProjectCardsContainer>
    ) : (
      <NoDataContainer>
        <SvgContainer>
          <NoDataDark />
        </SvgContainer>
        <CustomText
          text={t("app.noData")}
          type={TextType.P1}
          color={COLORS.MetalicSilver}
        />
      </NoDataContainer>
    );
  const tabs = [
    {
      id: 0,
      tabTitle: t("view.projects.ongoingProjectsTab"),
      content: ongoingProjects,
      totalPages: currentTotalPages,
      currentPage: currentPage,
      handlePageChange: handleCurrentPageChange,
    },
    {
      id: 1,
      tabTitle: t("view.projects.completedProjectsTab"),
      content: finishedProjects,
      totalPages: pastTotalPages,
      currentPage: pastPage,
      handlePageChange: handlePastPageChange,
    },
  ];

  // API REQUESTS

  // LIFE CYCLE

  const handleTabSelect = (selectedTabId: number) => {
    setSelectedTab(selectedTabId);
  };

  return (
    <ProjectsContainer>
      <TitleSectionWrapper>
        <BackToWrapper
          onClick={() => navigate(appRoutes.about)}
          biggerCursor={biggerCursor}
        >
          <BackToImage src={LeftArrow} alt="backIcon" />
          <BackToButton biggerCursor={biggerCursor}>
            <CustomText
              text={t("view.about.aboutDSU.button")}
              type={TextType.H5boldspaced}
              color={COLORS.RaisinBlack}
            />
          </BackToButton>
        </BackToWrapper>
        <TitleWrapper>
          <CustomText type={TextType.H2} text={t("view.projects.title")} />
        </TitleWrapper>
      </TitleSectionWrapper>

      <HeaderSectionWrapper>
        <HeaderTab tabs={tabs} onTabSelect={handleTabSelect} />
      </HeaderSectionWrapper>
      <ContentSectionWrapper>
        <InputContainer>
          <form
            onSubmit={(event) => {
              event.preventDefault();

              if (inputText !== "") {
                setOptions([{ option: "0 rezultate", id: 0, path: `/` }]);
              }
            }}
          >
            <InputAtom
              startAdornment={<ImgStyled src={RedLoop} alt="RedLoopIcon" />}
              endAdornment={
                <ButtonAtom
                  text={t("atoms.searchBar.search")}
                  type={ButtonType.ArrowButton}
                  // onClick={handleSearch}
                />
              }
              placeholder={t("atoms.searchBar.search")}
              isSearchBar={true}
              // options={options}
              onChange={(e) => setInputText(e.target.value)}
              isModal={true}
            />
          </form>
        </InputContainer>
        {tabs[selectedTab].content}
        {tabs[selectedTab].totalPages > 1 && (
          <Pagination
            totalPages={tabs[selectedTab].totalPages}
            currentPage={tabs[selectedTab].currentPage}
            onPageChange={tabs[selectedTab].handlePageChange}
          />
        )}
      </ContentSectionWrapper>
    </ProjectsContainer>
  );
};

export default Projects;
